/**
 * The screen registry: module key → the component that renders it.
 *
 * Every module in the registry has an entry here — the router looks a screen up by key and does
 * not fall back, so a module added without a screen fails the `screens.test` invariant rather than
 * rendering a placeholder that pretends to be a product.
 */
import type { FunctionComponent } from 'react';
import { Dashboard } from '../screens/Dashboard';
import { Funnel } from '../screens/Funnel';
import { MetricExplorer } from '../screens/MetricExplorer';
import { DispatchHealth } from '../screens/DispatchHealth';
import { CoverageLiquidity } from '../screens/CoverageLiquidity';
import { DispatchBoard } from '../screens/DispatchBoard';
import { Replacements } from '../screens/Replacements';
import { MatchingConsole } from '../screens/MatchingConsole';
import { Conflicts } from '../screens/Conflicts';
import { KycQueue } from '../screens/KycQueue';
import { PanditDirectory } from '../screens/PanditDirectory';
import { SkillsCoverage } from '../screens/SkillsCoverage';
import { Tickets } from '../screens/Tickets';
import { Disputes } from '../screens/Disputes';
import { Reviews } from '../screens/Reviews';
import { SlaPolicies } from '../screens/SlaPolicies';
import { ReplyMacros } from '../screens/ReplyMacros';
import { CommsPool } from '../screens/CommsPool';
import { Users } from '../screens/Users';
import { DsrWorkspace } from '../screens/DsrWorkspace';
import { PujaCatalogue } from '../screens/PujaCatalogue';
import { SearchDictionary } from '../screens/SearchDictionary';
import { Taxonomy } from '../screens/Taxonomy';
import { Pricing } from '../screens/Pricing';
import { Temples } from '../screens/Temples';
import { Cities } from '../screens/Cities';
import { Payments } from '../screens/Payments';
import { Webhooks } from '../screens/Webhooks';
import { Ledger } from '../screens/Ledger';
import { LedgerTransaction } from '../screens/LedgerTransaction';
import { MonthClose } from '../screens/MonthClose';
import { Payouts } from '../screens/Payouts';
import { Risk } from '../screens/Risk';
import { Approvals } from '../screens/Approvals';
import { RefundsQueue } from '../screens/RefundsQueue';
import { CancellationPolicies } from '../screens/CancellationPolicies';
import { NotificationHelp } from '../screens/NotificationHelp';
import { Tax } from '../screens/Tax';
import { Cms } from '../screens/Cms';
import { Redirects } from '../screens/Redirects';
import { SeoBudget } from '../screens/SeoBudget';
import { SeoRegistry } from '../screens/SeoRegistry';
import { SeoTemplates } from '../screens/SeoTemplates';
import { EnumLabels } from '../screens/EnumLabels';
import { RitualSchools } from '../screens/RitualSchools';
import { Samagri } from '../screens/Samagri';
import { Media } from '../screens/Media';
import { Offers } from '../screens/Offers';
import { Campaigns } from '../screens/Campaigns';
import { Settings } from '../screens/Settings';
import { Flags } from '../screens/Flags';
import { Abuse } from '../screens/Abuse';
import { Audit } from '../screens/Audit';
import { Admins } from '../screens/Admins';
import { MyAccount } from '../screens/MyAccount';
import { AcceptInvite } from '../screens/AcceptInvite';
import { Login } from '../screens/Login';

export const SCREENS: Readonly<Record<string, FunctionComponent>> = {
  dashboard: Dashboard,
  funnel: Funnel,
  metrics: MetricExplorer,
  health: DispatchHealth,
  supply: CoverageLiquidity,
  dispatch: DispatchBoard,
  replacements: Replacements,
  matching: MatchingConsole,
  conflicts: Conflicts,
  kyc: KycQueue,
  pandits: PanditDirectory,
  coverage: SkillsCoverage,
  tickets: Tickets,
  disputes: Disputes,
  reviews: Reviews,
  sla: SlaPolicies,
  macros: ReplyMacros,
  commspool: CommsPool,
  users: Users,
  dsr: DsrWorkspace,
  catalogue: PujaCatalogue,
  search: SearchDictionary,
  taxonomy: Taxonomy,
  pricing: Pricing,
  temples: Temples,
  geo: Cities,
  payments: Payments,
  webhooks: Webhooks,
  ledger: Ledger,
  ledgertxn: LedgerTransaction,
  close: MonthClose,
  payouts: Payouts,
  risk: Risk,
  approvals: Approvals,
  refunds: RefundsQueue,
  policies: CancellationPolicies,
  notifhelp: NotificationHelp,
  tax: Tax,
  cms: Cms,
  redirects: Redirects,
  seoregistry: SeoRegistry,
  seotemplates: SeoTemplates,
  seobudget: SeoBudget,
  labels: EnumLabels,
  ritualschools: RitualSchools,
  samagri: Samagri,
  media: Media,
  offers: Offers,
  campaigns: Campaigns,
  settings: Settings,
  flags: Flags,
  abuse: Abuse,
  audit: Audit,
  admins: Admins,
  account: MyAccount,
  invite: AcceptInvite,
  login: Login,
};
